/**
 * 🎯 Sistema di Referral Link Amazon per Wishare
 * 
 * Funzionalità:
 * - Generazione link Amazon con tracking ID wishspace06-21
 * - Moderazione contenuti con Azure Content Moderator
 * - Validazione URL e domini sicuri
 * - Tracciamento click (preparato per analytics)
 * - Supporto multi-TLD Amazon (.it, .co.uk, .de, .fr, .es, .com, ecc.)
 */

// 🔒 Lista parole vietate (aggiungere altre se necessario)
const bannedWords = [
  // Only truly harmful content - reduced list to prevent blocking legitimate products
  'porn', 'xxx', 'dildo', 'vibrator', 'bdsm',
  'erotic', 'masturbat', 'orgasm', 'hooker', 'prostitute', 'stripper',
  // Weapons/violence - only most dangerous
  'gun', 'rifle', 'pistol', 'ammunition', 'explosive', 'grenade',
  // Hard drugs only - removed 'drug' and 'cannabis' as they block legitimate products
  'cocaine', 'heroin', 'ecstasy', 'meth',
  // Hate/violence - only most severe
  'nazi', 'hitler', 'terrorism', 'bomb', 'murder', 'rape',
  // Scam/illegal - removed 'fake' as it blocks legitimate products like "faux leather"
  'counterfeit', 'illegal', 'stolen', 'pirated'
];

// 🚫 Domini vietati (solo contenuti realmente illegali o pericolosi)
const bannedDomains = [
  // Adult/pornografico - Major sites
  'pornhub.com', 'xvideos.com', 'xhamster.com', 'xnxx.com', 'youporn.com', 'redtube.com',
  'onlyfans.com', 'chaturbate.com', 'livejasmin.com', 'stripchat.com', 'cam4.com',
  'brazzers.com', 'realitykings.com', 'bangbros.com', 'naughtyamerica.com', 'eporner.com',
  'tube8.com', 'thumbzilla.com', 'spankbang.com', 'upornia.com', 'tnaflix.com',
  'xtube.com', 'drtuber.com', 'keezmovies.com', 'extremetube.com', 'pornhd.com',
  'beeg.com', 'youjizz.com', 'empflix.com', 'pornerbros.com', 'fapdu.com',
  'txxx.com', 'hqporner.com', '4tube.com', 'porngo.com', 'moviefap.com',
  'gotporn.com', 'xbef.com', 'analdin.com', 'vjav.com', 'vjav.com',
  'perfectgirls.net', 'txxx.com', 'freeomovie.com', 'porntrex.com', 'pornhat.com',
  'ok.xxx', 'xmoviesforyou.com', 'yespornplease.com', 'pornktube.com', 'hdzog.com',
  // Adult live cams
  'camsoda.com', 'myfreecams.com', 'bongacams.com', 'flirt4free.com', 'imlive.com',
  'streamate.com', 'xcams.com', 'jerkmate.com', 'slutroulette.com', 'shagle.com',
  // Adult retail/sex toys
  'cherrypink.ie', 'lovehoney.com', 'adameve.com', 'spencers.com', 'adulttoymegastore.com',
  'pureromance.com', 'hustlerhollywood.com', 'condomania.com', 'pleasurechest.com',
  'extremerestraints.com', 'stockroom.com', 'babeland.com', 'goodvibes.com',
  // Adult dating/hookup sites
  'adultfriendfinder.com', 'ashleymadison.com', 'fling.com', 'benaughty.com',
  'alt.com', 'naughtydate.com', 'passion.com', 'together2night.com',
  // Gambling illegale
  'bet365-illegal.com', 'casino-scam.com', 'illegal-gambling.com',
  // Droghe illegali
  'illegal-drugs.com', 'buy-cocaine.com', 'weed-shop-illegal.com',
  // Armi illegali
  'illegal-weapons.com', 'buy-guns-illegal.com',
  // URL shorteners sospetti
  'bit.ly', 'tinyurl.com', 'short.link'
];

// ✅ Categorie di siti SEMPRE permessi (approccio whitelist per categorie)
const allowedCategories = [
  // Fashion & Abbigliamento
  'zara.com', 'hm.com', 'uniqlo.com', 'gap.com', 'mango.com', 'bershka.com',
  'pullandbear.com', 'massimodutti.com', 'stradivarius.com', 'cos.com',
  'weekday.com', 'monki.com', 'arket.com', 'otherstories.com',
  // E-commerce principali
  'amazon.', 'ebay.', 'aliexpress.com', 'etsy.com', 'zalando.',
  'asos.com', 'farfetch.com', 'yoox.com', 'luisaviaroma.com',
  // Tecnologia
  'apple.com', 'samsung.com', 'sony.com', 'microsoft.com', 'google.com',
  // Sport & Lifestyle  
  'nike.com', 'adidas.com', 'puma.com', 'vans.com', 'converse.com',
  'jdsports.com', 'jd.com', 'jdsports.it', 'm.jdsports.it', 'jdsports.ie', 'jdsports.de',
  'footlocker.com', 'sizeer.com', 'office.co.uk', 'schuh.co.uk',
  // Casa & Design
  'ikea.com', 'westelm.com', 'cb2.com', 'crateandbarrel.com',
  // Qualsiasi dominio .it, .com, .co.uk, .de, .fr, .es (domini legittimi)
];

// ✅ Domini Amazon supportati
const amazonDomains = [
  'amazon.com', 'amazon.it', 'amazon.co.uk', 'amazon.de', 'amazon.fr',
  'amazon.es', 'amazon.ca', 'amazon.com.au', 'amazon.co.jp', 'amazon.in',
  'amazon.com.br', 'amazon.com.mx', 'amazon.nl', 'amazon.se', 'amazon.pl'
];

// 🆔 Tracking ID Amazon per Wishare
const AMAZON_TRACKING_ID = 'wishspace06-21';

/**
 * 🔍 Verifica se una parola è vietata
 * @param {string} text - Testo da controllare
 * @returns {boolean} - True se contiene parole vietate
 */

// Normalizzazione testo (minuscole, rimozione accenti, sostituzione non-lettere con spazi)
function normalizeText(s = "") {
  return s
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim();
}

// Costruisce una regex che fa match a parola intera (lettere/cifre come “parole”)
function buildBadWordsRegex(list) {
  const escaped = list.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  // confini di parola “custom”: inizio/fine o non-lettera/cifra
  return new RegExp(`(?:^|[^\\p{L}\\p{N}])(${escaped.join("|")})(?=$|[^\\p{L}\\p{N}])`, "iu");
}

/**
 * 🔍 Verifica se una parola è vietata (match a parola intera su testo normalizzato)
 * @param {string} text - Testo da controllare
 * @returns {boolean} - True se contiene parole vietate
 */
function isBannedWord(text) {
  if (!text || typeof text !== 'string') return false;
  const re = buildBadWordsRegex(bannedWords);
  return re.test(normalizeText(text));
}


/**
 * 🌐 Verifica se un dominio è vietato (approccio permissivo)
 * @param {string} url - URL da controllare
 * @returns {boolean} - True se il dominio è vietato
 */
function isBannedDomain(url) {
  try {
    const domain = new URL(url).hostname.toLowerCase();

    // Controlla solo i domini esplicitamente vietati (contenuti illegali)
    const isExplicitlyBanned = bannedDomains.some(banned => 
      domain.includes(banned.toLowerCase())
    );

    if (isExplicitlyBanned) {
      console.log('❌ Dominio vietato:', domain);
      return true;
    }

    // Controllo parole estremamente pericolose solo nel dominio
    const extremelyDangerous = ['porn', 'xxx', 'sex', 'adult', 'casino', 'gambling', 'illegal', 'scam', 'dildo', 'vibrator', 'erotic', 'fetish', 'bdsm'];
    const hasExtremeContent = extremelyDangerous.some(word => {
      // Controlla che la parola sia parte del dominio principale, non solo un parametro
      const domainParts = domain.split('.');
      const found = domainParts.some(part => part.includes(word));
      if (found) {
        console.log('❌ DEBUGGING: Found dangerous word:', word, 'in domain parts:', domainParts);
      }
      return found;
    });
    console.log('🔍 DEBUGGING: Extreme content check result:', hasExtremeContent);

    if (hasExtremeContent) {
      console.log('❌ Dominio con contenuto pericoloso:', domain);
      return true;
    }

    // TUTTI gli altri domini sono permessi (approccio permissivo)
    console.log('✅ Dominio permesso:', domain);
    return false;

  } catch (error) {
    console.error('❌ Errore nel parsing del dominio:', error);
    return true; // URL malformato = vietato per sicurezza
  }
}

/**
 * 🛡️ Sanifica un URL rimuovendo parametri pericolosi
 * @param {string} url - URL da sanificare
 * @returns {string} - URL pulito
 */
function sanitizeUrl(url) {
  try {
    const urlObj = new URL(url);

    // Rimuovi parametri potenzialmente pericolosi
    const dangerousParams = ['javascript', 'data', 'vbscript', 'onclick'];
    dangerousParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });

    return urlObj.toString();
  } catch (error) {
    console.error('❌ Errore nella sanificazione URL:', error);
    return url;
  }
}

/**
 * 🏪 Verifica se un URL è di Amazon
 * @param {string} url - URL da verificare
 * @returns {boolean} - True se è un dominio Amazon
 */
export function isAmazonUrl(url) {
  if (!url || typeof url !== 'string') return false;

  try {
    const domain = new URL(url).hostname.toLowerCase();
    console.log('🔍 Controllo dominio:', domain);

    const isAmazon = amazonDomains.some(amazonDomain => 
      domain === amazonDomain || 
      domain.endsWith('.' + amazonDomain) ||
      domain.includes('amazon')
    );

    console.log('🎯 È Amazon?', isAmazon);
    return isAmazon;
  } catch (error) {
    console.error('❌ Errore nel controllo dominio Amazon:', error);
    return false;
  }
}

/**
 * ✅ Valida se un URL prodotto è sicuro e consentito
 * @param {string} url - URL da validare
 * @returns {object} - {valid: boolean, reason?: string}
 */

export function isValidProductUrl(url) {
  if (!url || typeof url !== 'string') {
    return { valid: false, reasonKey: 'errors.invalidUrl' };
  }

  // 🎯 LENIENT: Only check for malformed URLs - allow any domain
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
    // Must be HTTP/HTTPS
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return { valid: false, reasonKey: 'errors.invalidProtocol' };
    }
  } catch (error) {
    return { valid: false, reasonKey: 'errors.malformedUrl' };
  }

  // 🚫 CHECK BANNED DOMAINS - Block adult/illegal sites
  const domain = parsedUrl.hostname.toLowerCase();
  console.log('🔍 Checking domain against banned list:', domain);
  
  for (const bannedDomain of bannedDomains) {
    if (domain === bannedDomain || domain.endsWith('.' + bannedDomain) || domain.includes(bannedDomain)) {
      console.log('❌ BANNED DOMAIN DETECTED:', domain, '- matches:', bannedDomain);
      return { valid: false, reasonKey: 'errors.bannedDomain' };
    }
  }

  // 🎯 LENIENT: Only check for extremely explicit harmful content in URL path - much smaller list
  const urlLower = url.toLowerCase();
  const extremeAdultPatterns = [
    '/xxx', '/porn', '/dildo', '/vibrator'
    // Removed most patterns to allow legitimate fashion/product sites
  ];
  
  for (const pattern of extremeAdultPatterns) {
    if (urlLower.includes(pattern)) {
      console.log('❌ URL contains explicit adult content pattern:', pattern);
      return { valid: false, reasonKey: 'errors.urlAdultContent' };
    }
  }

  // ✅ Non applichiamo ban-words generiche sull'URL per evitare falsi positivi (es. 'massimoDutti' -> 'ass')
  console.log('✅ URL validation passed:', domain);
  return { valid: true };
}

/**
 * 🔗 Genera un link Amazon con tracking ID
 * @param {string} productUrl - URL originale del prodotto Amazon
 * @param {string} clickRef - Riferimento opzionale per il click (es: wishlistId)
 * @returns {string} - Link Amazon con tracking
 */
export function generateAmazonLink(productUrl, clickRef = null) {
  if (!isAmazonUrl(productUrl)) {
    console.warn('⚠️ URL non è di Amazon:', productUrl);
    return productUrl;
  }

  try {
    const url = new URL(productUrl);

    // Rimuovi parametri esistenti di tracking per evitare conflitti
    url.searchParams.delete('tag');
    url.searchParams.delete('ref');
    url.searchParams.delete('linkCode');
    url.searchParams.delete('camp');
    url.searchParams.delete('creative');
    url.searchParams.delete('creativeASIN');

    // Aggiungi il nostro tracking ID Amazon
    url.searchParams.set('tag', AMAZON_TRACKING_ID);

    // Aggiungi riferimento opzionale per tracciamento interno
    if (clickRef) {
      url.searchParams.set('ref', `wishspace_${clickRef}`);
    }

    const finalUrl = url.toString();
    console.log('✅ Link Amazon generato con tracking:', finalUrl);
    console.log('🔗 Tracking ID applicato:', AMAZON_TRACKING_ID);

    return finalUrl;

  } catch (error) {
    console.error('❌ Errore nella generazione link Amazon:', error);
    return productUrl; // Fallback sull'URL originale
  }
}

/**
 * 🤖 Moderazione contenuti con Azure Content Moderator
 * @param {string} text - Testo da moderare
 * @param {string} imageUrl - URL immagine da moderare (opzionale)
 * @returns {Promise<object>} - {safe: boolean, reason?: string}
 */
export async function moderateWithAzure(text, imageUrl = null) {
  try {
    console.log('🔍 Avvio moderazione Azure Content Safety...');

    // Controlla parole vietate nel testo
    if (text && isBannedWord(text)) {
      console.log('❌ Parole vietate rilevate nel testo');
      return { 
        safe: false, 
        reason: 'Testo contiene contenuto non consentito' 
      };
    }

    // Controlla lunghezza testo (evita spam)
    if (text && text.length > 1000) {
      return { 
        safe: false, 
        reason: 'Testo troppo lungo (max 1000 caratteri)' 
      };
    }

    // Se c'è un'immagine, verifica che l'URL sia valido (skip per immagini base64)
    if (imageUrl && !imageUrl.startsWith('data:')) {
      const urlValidation = isValidProductUrl(imageUrl);
      if (!urlValidation.valid) {
        return { 
          safe: false, 
          reason: `Immagine non valida: ${urlValidation.reason}` 
        };
      }
    }

    // Chiamata REALE ad Azure Content Safety API per testo
    if (text) {
      try {
        const endpoint = "https://wishspace-content-safety.cognitiveservices.azure.com/";
        const apiKey = "4slxwOnPHWERPLRUKKocJWa0zg7RArecO72mv8a9igVsjJQ5TZ7IJQQJ99BFAC5RqLJXJ3w3AAAHACOG6CNA";

        const response = await fetch(`${endpoint}contentsafety/text:analyze?api-version=2023-10-01`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Ocp-Apim-Subscription-Key": apiKey
          },
          body: JSON.stringify({
            text: text,
            categories: ["Sexual", "Violence", "Hate", "SelfHarm"],
            haltOnBlocklistHit: true
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Risposta Azure ricevuta:', result);

          // Enhanced clothing detection and relaxed thresholds
          const clothingKeywords = [
            'clothing','apparel','garment','jacket','hoodie','sweater','shirt','t-shirt',
            'coat','trousers','pants','jeans','shorts','skirt','dress','shoes','sneakers',
            'bag','fashion','wear','fabric','textile','cotton','denim','leather'
          ];

          // Check text content for clothing indicators
          const textContent = (text || '').toLowerCase();
          const isClothingRelated = clothingKeywords.some(keyword => textContent.includes(keyword));

          let maxSeverity = 0;
          let maxCategory = 'None';
          let hasViolenceFlag = false;

          for (const cat of (result?.categoriesAnalysis || [])) {
            if (typeof cat?.severity === 'number' && cat.severity > maxSeverity) {
              maxSeverity = cat.severity;
              maxCategory = cat.category || 'Unknown';
            }

            // Track violence flags (often triggered by dark/black items)
            if (cat.category === 'Violence' && cat.severity > 0) {
              hasViolenceFlag = true;
            }
          }

          // Much more relaxed thresholds - only block truly dangerous content
          const ADULT_THRESHOLD = 7;    // Very high threshold
          const GORE_THRESHOLD = 7;     // Very high threshold  
          const HATE_THRESHOLD = 6;     // High threshold
          const SELFHARM_THRESHOLD = 2; // Only block clear self-harm

          // Special handling for clothing items
          if (isClothingRelated && hasViolenceFlag && maxSeverity <= 6) {
            console.log('✅ Clothing item with low violence score - allowing:', { maxSeverity, maxCategory });
            return { safe: true };
          }

          // Apply strict thresholds only for truly dangerous content
          if (maxCategory === 'Sexual' && maxSeverity >= ADULT_THRESHOLD) {
            console.log('❌ Contenuto adulto rilevato da Azure', maxCategory, maxSeverity);
            return { 
              safe: false, 
              reason: 'Contenuto inappropriato rilevato dal sistema di moderazione' 
            };
          }

          if (maxCategory === 'Violence' && maxSeverity >= GORE_THRESHOLD && !isClothingRelated) {
            console.log('❌ Contenuto violento rilevato da Azure', maxCategory, maxSeverity);
            return { 
              safe: false, 
              reason: 'Contenuto inappropriato rilevato dal sistema di moderazione' 
            };
          }

          if (maxCategory === 'Hate' && maxSeverity >= HATE_THRESHOLD) {
            console.log('❌ Contenuto di odio rilevato da Azure', maxCategory, maxSeverity);
            return { 
              safe: false, 
              reason: 'Contenuto inappropriato rilevato dal sistema di moderazione' 
            };
          }

          if (maxCategory === 'SelfHarm' && maxSeverity >= SELFHARM_THRESHOLD) {
            console.log('❌ Contenuto autolesionista rilevato da Azure', maxCategory, maxSeverity);
            return { 
              safe: false, 
              reason: 'Contenuto inappropriato rilevato dal sistema di moderazione' 
            };
          }
        } else {
          console.warn('⚠️ Azure API non disponibile, applicando moderazione locale');
          // Fallback alla moderazione locale più severa
          return { safe: true }; // Solo se supera tutti i controlli locali
        }
      } catch (azureError) {
        console.warn('⚠️ Errore Azure API, applicando moderazione locale:', azureError);
      }
    }

    console.log('✅ Contenuto approvato dalla moderazione');
    return { safe: true };

  } catch (error) {
    console.error('❌ Errore nella moderazione:', error);
    // APPROCCIO SICURO: in caso di errore, BLOCCA il contenuto
    return { 
      safe: false, 
      reason: 'Errore nel servizio di moderazione - contenuto bloccato per sicurezza' 
    };
  }
}

/**
 * 🔗 Genera link affiliato (Amazon o fallback)
 * @param {object} product - Oggetto prodotto con URL
 * @returns {string} - Link finale (affiliato se Amazon, originale altrimenti)
 */
export function getAffiliateLink(product) {
  if (!product || !product.url) {
    console.warn('⚠️ Prodotto senza URL:', product);
    return '#';
  }

  const sanitizedUrl = sanitizeUrl(product.url);
  console.log('🔍 Controllo URL per affiliazione:', sanitizedUrl);

  // Se è Amazon, genera link con tracking
  if (isAmazonUrl(sanitizedUrl)) {
    console.log('✅ URL Amazon riconosciuto, generando link affiliato...');
    const affiliateLink = generateAmazonLink(sanitizedUrl, product.wishlistId || product.id || 'direct');
    console.log('🎯 Link affiliato finale:', affiliateLink);
    return affiliateLink;
  } else {
    console.log('ℹ️ URL non Amazon, mantengo originale:', sanitizedUrl);
  }

  // TODO: Aggiungere supporto per altri network di affiliazione
  // if (isAwinPartner(sanitizedUrl)) {
  //   return generateAwinLink(sanitizedUrl);
  // }
  // if (isImpactPartner(sanitizedUrl)) {
  //   return generateImpactLink(sanitizedUrl);
  // }

  // Fallback sull'URL originale
  return sanitizedUrl;
}

/**
 * 📊 Traccia un click su un link affiliato
 * @param {string} productId - ID del prodotto
 * @param {string} wishlistId - ID della wishlist
 * @param {string} linkType - Tipo di link (amazon, awin, impact, direct)
 */
export function trackClick(productId, wishlistId, linkType = 'direct') {
  try {
    const clickData = {
      productId: productId || 'unknown',
      wishlistId: wishlistId || 'unknown',
      linkType,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      trackingId: AMAZON_TRACKING_ID
    };

    console.log('📊 Click affiliato tracciato:', clickData);
    console.log('🎯 Tracking ID utilizzato:', AMAZON_TRACKING_ID);

    // TODO: Implementare salvataggio su Firestore per analytics
    // await addDoc(collection(db, 'affiliate_clicks'), clickData);

    // TODO: Inviare a Google Analytics o altro servizio di tracking
    // gtag('event', 'affiliate_click', { ...clickData });

  } catch (error) {
    console.error('❌ Errore nel tracciamento click:', error);
  }
}

// 🚧 Placeholder per future integrazioni (Settimana 4)

/**
 * 🔗 Genera link Awin (placeholder)
 * @param {string} productUrl - URL prodotto
 * @returns {string} - Link Awin (da implementare)
 */
export function generateAwinLink(productUrl) {
  // TODO: Implementare integrazione Awin
  console.log('🚧 generateAwinLink - Da implementare:', productUrl);
  return productUrl;
}

/**
 * 🔗 Genera link Impact (placeholder)
 * @param {string} productUrl - URL prodotto  
 * @returns {string} - Link Impact (da implementare)
 */
export function generateImpactLink(productUrl) {
  // TODO: Implementare integrazione Impact
  console.log('🚧 generateImpactLink - Da implementare:', productUrl);
  return productUrl;
}

/**
 * 🤝 Verifica se un URL è partner Awin (placeholder)
 * @param {string} url - URL da verificare
 * @returns {boolean} - True se è partner Awin
 */
export function isAwinPartner(url) {
  // TODO: Implementare lista partner Awin
  return false;
}

/**
 * 🤝 Verifica se un URL è partner Impact (placeholder)
 * @param {string} url - URL da verificare
 * @returns {boolean} - True se è partner Impact
 */
export function isImpactPartner(url) {
  // TODO: Implementare lista partner Impact
  return false;
}

// Alias per backward compatibility
export const isBannedWordExact = isBannedWord;

export default {
  isAmazonUrl,
  isValidProductUrl,
  generateAmazonLink,
  moderateWithAzure,
  getAffiliateLink,
  trackClick,
  generateAwinLink,
  generateImpactLink,
  isAwinPartner,
  isImpactPartner,
  isBannedWord,
  isBannedWordExact
};